/**
 * Fills the official Fannie Mae 1076 with the questionnaire's answers.
 *
 * The lender receives the form they recognise: the PDF Fannie Mae publishes,
 * with its 174 AcroForm fields written by name and its boxes ticked, then
 * flattened so nothing can be edited on the way. Which answer lands in which
 * field is the `pdf` of every field in spec.mjs; nothing here knows a field
 * by name.
 *
 * Runs in the browser (the Process screen) and in Node (the check script):
 * the PDF bytes can be handed in, or fetched from public/forms.
 */

import { PDFDocument, PDFTextField, StandardFonts, TextAlignment } from "pdf-lib";
import type { PDFForm } from "pdf-lib";

import {
  PDF_FILE,
  PDF_FROM_CONTEXT,
  allFields,
  pdfTargets,
  radioState,
} from "./spec.mjs";
import type { FlatField } from "./spec.mjs";

/** Values the generator writes that come from the order, not from a field. */
export interface FillContext {
  /** "Complete and return this form by": the order's promised delivery date. */
  return_by?: string | null;
  [key: string]: string | null | undefined;
}

export interface FillReport {
  /** Text fields written. */
  text: number;
  /** Checkboxes ticked and radio options selected. */
  boxes: number;
  /** Answers that had no PDF field, or a field that refused the value. */
  problems: string[];
}

export interface FillOptions {
  /** The blank form. When absent it is fetched from `pdfUrl`. */
  pdfBytes?: ArrayBuffer | Uint8Array;
  /** Where to fetch the blank form; defaults to public/forms/<PDF_FILE>. */
  pdfUrl?: string;
  /** Flatten after filling so the result is not editable. Default true. */
  flatten?: boolean;
}

/** "Yes", "true", "on", "1" — the ways a checked box can have been stored. */
export function isYes(value: unknown): boolean {
  return /^(yes|y|true|on|1)$/i.test(String(value ?? "").trim());
}

/**
 * Dates are stored ISO (2026-09-15) and the form is American: 09/15/2026.
 * Anything that is not an ISO date is returned as it came.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(value).trim();
}

/** Long answers in short boxes: shrink the type rather than clip the text. */
function fontSizeFor(value: string): number | null {
  if (value.length > 90) return 6;
  if (value.length > 55) return 7;
  if (value.length > 35) return 8;
  return null;
}

export function defaultPdfUrl(): string {
  const base = (import.meta as any).env?.BASE_URL ?? "/";
  return `${base.endsWith("/") ? base : base + "/"}forms/${PDF_FILE}`;
}

function writeText(
  form: PDFForm,
  target: string,
  value: string,
  align: TextAlignment = TextAlignment.Left,
) {
  const field = form.getTextField(target);
  const size = fontSizeFor(value);
  if (size) field.setFontSize(size);
  if (align !== TextAlignment.Left) field.setAlignment(align);
  field.setText(value);
}

function writeField(form: PDFForm, field: FlatField, rawValue: string, report: FillReport) {
  const value = field.type === "date" ? formatDate(rawValue) : rawValue.trim();
  if (value === "") return;

  for (const target of pdfTargets(field)) {
    try {
      if (field.type === "checkbox") {
        const box = form.getCheckBox(target);
        if (isYes(value)) {
          box.check();
          report.boxes++;
        } else {
          box.uncheck();
        }
      } else if (field.type === "select" && typeof field.pdf === "object" && !Array.isArray(field.pdf)) {
        const state = radioState(field, value);
        if (state === null) continue; // "N/A": the group stays blank
        form.getRadioGroup(target).select(state);
        report.boxes++;
      } else {
        writeText(form, target, value);
        report.text++;
      }
    } catch (error) {
      report.problems.push(`${field.name} → "${target}": ${(error as Error).message}`);
    }
  }
}

/**
 * Fills the blank 1076 with `answers` (spec field name → value, as the Process
 * screen holds them) and `context`, and returns the PDF bytes.
 */
export async function fillForm1076(
  answers: Record<string, string | null | undefined>,
  context: FillContext = {},
  options: FillOptions = {},
): Promise<{ bytes: Uint8Array; report: FillReport }> {
  let pdfBytes = options.pdfBytes;
  if (!pdfBytes) {
    const url = options.pdfUrl ?? defaultPdfUrl();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Could not load the blank form from ${url} (${response.status})`);
    }
    pdfBytes = await response.arrayBuffer();
  }

  const document = await PDFDocument.load(pdfBytes);
  const form = document.getForm();
  const report: FillReport = { text: 0, boxes: 0, problems: [] };

  for (const field of allFields()) {
    const raw = answers[field.name];
    if (raw === null || raw === undefined || String(raw).trim() === "") continue;
    writeField(form, field, String(raw), report);
  }

  for (const [target, key] of Object.entries(PDF_FROM_CONTEXT)) {
    const value = context[key];
    if (!value) continue;
    try {
      // The return-by date sits on a printed underline; centred is how a
      // person would write it there.
      writeText(form, target, String(value), TextAlignment.Center);
      report.text++;
    } catch (error) {
      report.problems.push(`${key} → "${target}": ${(error as Error).message}`);
    }
  }

  if (options.flatten !== false) {
    // Text fields get their appearance regenerated in Helvetica: the blank
    // form has none, so without this the values would be invisible in some
    // viewers. Boxes and radio groups are left alone on purpose — the form
    // ships its own on/off drawings for every one of them, and pdf-lib's
    // restyling gets the flood-coverage options wrong. Then everything is
    // baked into the page so the lender cannot edit it.
    const helvetica = await document.embedFont(StandardFonts.Helvetica);
    for (const field of form.getFields()) {
      if (field instanceof PDFTextField) field.updateAppearances(helvetica);
    }
    form.flatten({ updateFieldAppearances: false });
  }

  const bytes = await document.save();
  return { bytes, report };
}
